import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Meta, Title } from '@angular/platform-browser';
import { FooterComponent } from '../shared/footer/footer.component';
import { environment } from '../../environments/environment';
import {
  DAYS_PER_MONTH,
  DEFAULT_FILLERS_TEXT,
  DEFAULT_FOOD_ID,
  FOODS,
  PET_WANTS,
  QUICK_CUPS,
  VALUE_POINT_NAMES
} from './cost-calculator.data';
import { FoodOption, ValuePointKey } from './cost-calculator.types';

@Component({
  selector: 'app-cost-calculator',
  imports: [FooterComponent, MatFormFieldModule, MatSelectModule],
  templateUrl: './cost-calculator.component.html',
  styleUrl: './cost-calculator.component.scss'
})
export class CostCalculatorComponent implements OnInit {

  #meta = inject(Meta);
  #title = inject(Title);

  readonly foods = FOODS;
  readonly petWants = PET_WANTS;
  readonly quickCups = QUICK_CUPS;

  /*raw form values, kept as typed so the parsing matches the approved prototype*/
  selectedFoodId = signal(DEFAULT_FOOD_ID);
  cupsPerDayRaw = signal('2');
  manualNameRaw = signal('Custom food');
  manualKcalRaw = signal('380');
  manualCostCupRaw = signal('0.55');
  manualCupsPoundRaw = signal('4.2');

  isCustomFood = computed(() => Boolean(this.#selectedOption().isCustom));

  currentFood = computed<FoodOption>(() => {
    const selected = this.#selectedOption();
    if (!selected.isCustom) return selected;

    return {
      ...selected,
      name: this.manualNameRaw().trim() || 'Custom food',
      kcalPerCup: Math.max(1, parseFloat(this.manualKcalRaw()) || 380),
      costPerCup: Math.max(0, parseFloat(this.manualCostCupRaw()) || 0.55),
      cupsPerPound: Math.max(0.1, parseFloat(this.manualCupsPoundRaw()) || 4.2),
      tags: ['custom'],
      fillers: []
    };
  });

  cupsPerDay = computed(() => Math.max(0.1, parseFloat(this.cupsPerDayRaw()) || 0.1));

  metrics = computed(() => {
    const food = this.currentFood();
    const cupsPerDay = this.cupsPerDay();

    const currentDailyCalories = cupsPerDay * food.kcalPerCup;
    const petWantsCups = currentDailyCalories / PET_WANTS.kcalPerCup;

    const currentDailyCost = cupsPerDay * food.costPerCup;
    const petWantsDailyCost = petWantsCups * PET_WANTS.costPerCup;
    const currentMonthlyCost = currentDailyCost * DAYS_PER_MONTH;
    const petWantsMonthlyCost = petWantsDailyCost * DAYS_PER_MONTH;

    return {
      currentDailyCost,
      petWantsDailyCost,
      currentMonthlyCost,
      petWantsMonthlyCost,
      dailyDifference: petWantsDailyCost - currentDailyCost,
      monthlyDifference: petWantsMonthlyCost - currentMonthlyCost,
      servingReductionPct: cupsPerDay > 0 ? (1 - (petWantsCups / cupsPerDay)) : 0
    };
  });

  currentDailyCostLabel = computed(() => `${this.#formatMoney(this.metrics().currentDailyCost)}/day`);
  petWantsDailyCostLabel = computed(() => `${this.#formatMoney(this.metrics().petWantsDailyCost)}/day`);
  currentMonthlyEstimate = computed(() => `${this.#formatMoney(this.metrics().currentMonthlyCost)} estimated per month`);
  petWantsMonthlyEstimate = computed(() => `${this.#formatMoney(this.metrics().petWantsMonthlyCost)} estimated per month`);

  difference = computed(() => {
    const { dailyDifference, monthlyDifference } = this.metrics();
    const absDaily = Math.abs(dailyDifference);
    const absMonthly = this.#formatMoney(Math.abs(monthlyDifference));

    if (absDaily < 0.05) {
      return {
        headline: 'Costs are about the same per day.',
        sub: `That is approximately ${absMonthly} per month in difference, before considering the convenience and freshness differences.`
      };
    }

    const sub = `That is approximately ${absMonthly} per month, before considering the convenience and freshness differences.`;

    return dailyDifference < 0
      ? { headline: `Save about ${this.#formatMoney(absDaily)} per day.`, sub }
      : { headline: `Pet Wants is about ${this.#formatMoney(absDaily)} more per day.`, sub };
  });

  highlightedPoints = computed<ValuePointKey[]>(() => {
    const { tags, fillers } = this.currentFood();
    const { servingReductionPct, dailyDifference } = this.metrics();
    const keys: ValuePointKey[] = [];

    if (tags.includes('national') || tags.includes('vet') || tags.includes('premium')) {
      keys.push('freshness');
    }
    if (fillers.length) {
      keys.push('fillers');
    }
    if (servingReductionPct >= 0.08) {
      keys.push('smallerServing');
    }
    if (Math.abs(dailyDifference) <= 0.4 || dailyDifference > 0 || tags.includes('premium') || tags.includes('vet')) {
      keys.push('local');
    }
    if (!keys.length) {
      keys.push('freshness');
    }

    return keys;
  });

  fillersText = computed(() => {
    const { fillers } = this.currentFood();
    return fillers.length
      ? `Compared with Pet Wants, this selected food includes ${this.#formatList(fillers)}.`
      : DEFAULT_FILLERS_TEXT;
  });

  relevanceText = computed(() => {
    const points = this.highlightedPoints().map((key) => VALUE_POINT_NAMES[key]);
    return `For ${this.currentFood().name}, the calculator is especially emphasizing ${this.#formatList(points)}.`;
  });

  ngOnInit(): void {
    this.#setUpSEOTags();
  }

  isHighlighted(key: ValuePointKey): boolean {
    return this.highlightedPoints().includes(key);
  }

  onFoodChange(foodId: string) {
    this.selectedFoodId.set(foodId);
  }

  onCupsChange(event: Event) {
    this.cupsPerDayRaw.set(this.#readValue(event));
  }

  onManualNameChange(event: Event) {
    this.manualNameRaw.set(this.#readValue(event));
  }

  onManualKcalChange(event: Event) {
    this.manualKcalRaw.set(this.#readValue(event));
  }

  onManualCostCupChange(event: Event) {
    this.manualCostCupRaw.set(this.#readValue(event));
  }

  onManualCupsPoundChange(event: Event) {
    this.manualCupsPoundRaw.set(this.#readValue(event));
  }

  setCupsPerDay(cups: number) {
    this.cupsPerDayRaw.set(`${cups}`);
  }

  #selectedOption(): FoodOption {
    return this.foods.find((food) => food.id === this.selectedFoodId()) || this.foods[0];
  }

  #readValue(event: Event): string {
    return (event.target as HTMLInputElement | HTMLSelectElement).value;
  }

  #formatMoney(value: number): string {
    return `$${value.toFixed(2)}`;
  }

  #formatList(items: string[]): string {
    if (!items.length) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
  }

  #setUpSEOTags() {
    const { baseTitle, baseURL, type } = environment.config.seo;
    const url = 'cost-calculator';
    const component = 'Daily Cost Calculator';
    const title = `${component} | ${baseTitle}`;
    const description = 'Compare your current dog food\'s daily cost to the Pet Wants equivalent and discover the benefits of consistent, high quality nutrition.';

    // Page title
    this.#title.setTitle(title);

    this.#meta.addTag({ name: 'description', content: description });

    // Open Graph for social networks:
    this.#meta.addTag({ property: 'og:title', content: title });
    this.#meta.addTag({ property: 'og:description', content: description });
    this.#meta.addTag({ property: 'og:url', content: `${baseURL}${url}` });
    this.#meta.addTag({ property: 'og:type', content: type });
  }
}
